import React, { useEffect, useState, useCallback } from 'react';
import { Card, Button, Alert, Spinner, Form, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchSettings, saveSettings, reloadConfig } from '../../services/adminApi';
import { useAuth } from '../../services/AuthContext';

const CATEGORIES = [
    { id: 'tax',          label: 'Tax & Allowances' },
    { id: 'projection',   label: 'Projections'      },
    { id: 'contribution', label: 'Contributions'    },
    { id: 'risk',         label: 'Risk'             },
    { id: 'compliance',   label: 'Compliance'       },
];

// label, unit, multiplier (stored × multiplier = displayed value), type ('bool' for booleans)
const FIELD_META = {
    // ── Tax ──────────────────────────────────────────────────────────────────
    pension_annual_allowance:          { label: 'Pension Annual Allowance',          unit: '£'    },
    isa_annual_allowance:              { label: 'ISA Annual Allowance',               unit: '£'    },
    personal_allowance:                { label: 'Personal Allowance',                 unit: '£'    },
    basic_rate_threshold:              { label: 'Basic Rate Threshold',               unit: '£'    },
    higher_rate_threshold:             { label: 'Higher Rate Threshold',              unit: '£'    },
    high_earner_warning_threshold:     { label: 'High Earner Warning Threshold',      unit: '£'    },
    taper_threshold_income:            { label: 'Taper Threshold Income',             unit: '£'    },
    taper_adjusted_income:             { label: 'Taper Adjusted Income',              unit: '£'    },
    taper_minimum_allowance:           { label: 'Taper Minimum Allowance',            unit: '£'    },
    pension_minimum_contribution:      { label: 'Pension Minimum Contribution',       unit: '£'    },
    basic_rate_relief:                 { label: 'Basic Rate Relief',                  unit: '%',  multiplier: 100 },
    higher_rate_relief:                { label: 'Higher Rate Relief',                 unit: '%',  multiplier: 100 },
    additional_rate_relief:            { label: 'Additional Rate Relief',             unit: '%',  multiplier: 100 },
    relief_arrangement:                { label: 'Tax Relief Arrangement',             type: 'select',
                                         options: [['relief_at_source', 'Relief at source'], ['net_pay', 'Net pay'], ['salary_sacrifice', 'Salary sacrifice']] },
    ni_main_rate:                      { label: 'Employee NI Main Rate',              unit: '%',  multiplier: 100 },
    ni_upper_rate:                     { label: 'Employee NI Upper Rate',             unit: '%',  multiplier: 100 },
    ni_primary_threshold:              { label: 'NI Primary Threshold',               unit: '£'    },
    ni_upper_earnings_limit:           { label: 'NI Upper Earnings Limit',            unit: '£'    },
    scottish_starter_rate:             { label: 'Scottish Starter Rate',              unit: '%',  multiplier: 100 },
    scottish_basic_rate:               { label: 'Scottish Basic Rate',                unit: '%',  multiplier: 100 },
    scottish_intermediate_rate:        { label: 'Scottish Intermediate Rate',         unit: '%',  multiplier: 100 },
    scottish_higher_rate:              { label: 'Scottish Higher Rate',               unit: '%',  multiplier: 100 },
    scottish_advanced_rate:            { label: 'Scottish Advanced Rate',             unit: '%',  multiplier: 100 },
    scottish_top_rate:                 { label: 'Scottish Top Rate',                  unit: '%',  multiplier: 100 },
    scottish_basic_threshold:          { label: 'Scottish Basic Threshold',           unit: '£'    },
    scottish_intermediate_threshold:   { label: 'Scottish Intermediate Threshold',    unit: '£'    },
    scottish_higher_threshold:         { label: 'Scottish Higher Threshold',          unit: '£'    },
    scottish_advanced_threshold:       { label: 'Scottish Advanced Threshold',        unit: '£'    },
    state_pension_age:                 { label: 'State Pension Age',                  unit: 'yrs'  },
    state_pension_annual:              { label: 'State Pension (Annual)',              unit: '£'    },
    // ── Projections ──────────────────────────────────────────────────────────
    default_growth_rate:               { label: 'Default Growth Rate',                unit: '%',  multiplier: 100 },
    conservative_growth_rate:          { label: 'Conservative Growth Rate',           unit: '%',  multiplier: 100 },
    moderate_growth_rate:              { label: 'Moderate Growth Rate',               unit: '%',  multiplier: 100 },
    aggressive_growth_rate:            { label: 'Aggressive Growth Rate',             unit: '%',  multiplier: 100 },
    inflation_rate:                    { label: 'Inflation Rate',                     unit: '%',  multiplier: 100 },
    overfunding_threshold:             { label: 'Overfunding Threshold',              unit: '%',  multiplier: 100 },
    underfunding_threshold:            { label: 'Underfunding Threshold',             unit: '%',  multiplier: 100 },
    safe_withdrawal_rate:              { label: 'Safe Withdrawal Rate',               unit: '%',  multiplier: 100 },
    annuity_rate:                      { label: 'Annuity Rate',                       unit: '%',  multiplier: 100 },
    carry_forward_years:               { label: 'Carry Forward Years',                unit: 'yrs'  },
    // ── Contributions ────────────────────────────────────────────────────────
    max_pct_net_income:                { label: 'Max % of Net Income',                unit: '%',  multiplier: 100 },
    max_pct_disposable:                { label: 'Max % of Disposable Income',         unit: '%',  multiplier: 100 },
    max_total_pct_disposable:          { label: 'Max Total % of Disposable',          unit: '%',  multiplier: 100 },
    max_monthly_contribution:          { label: 'Max Monthly Contribution',           unit: '£'    },
    min_monthly_contribution:          { label: 'Min Monthly Contribution',           unit: '£'    },
    min_disposable_for_advice:         { label: 'Min Disposable for Advice',          unit: '£'    },
    default_employer_match_cap:        { label: 'Default Employer Match Cap',         unit: '%',  multiplier: 100 },
    affordability_buffer:              { label: 'Affordability Buffer',               unit: '%',  multiplier: 100 },
    // ── Risk ─────────────────────────────────────────────────────────────────
    tolerance_min:                     { label: 'Tolerance Min Score',                unit: ''     },
    tolerance_max:                     { label: 'Tolerance Max Score',                unit: ''     },
    capacity_min:                      { label: 'Capacity Min Score',                 unit: ''     },
    capacity_max:                      { label: 'Capacity Max Score',                 unit: ''     },
    near_retirement_threshold_years:   { label: 'Near Retirement Threshold',          unit: 'yrs'  },
    near_retirement_adjustment:        { label: 'Near Retirement Adjustment',         unit: '×'    },
    approaching_retirement_years:      { label: 'Approaching Retirement Threshold',   unit: 'yrs'  },
    approaching_retirement_adjustment: { label: 'Approaching Retirement Adjustment',  unit: '×'    },
    many_dependents_threshold:         { label: 'Many Dependants Threshold',          unit: ''     },
    many_dependents_adjustment:        { label: 'Many Dependants Adjustment',         unit: '×'    },
    some_dependents_adjustment:        { label: 'Some Dependants Adjustment',         unit: '×'    },
    very_tight_disposable_pct:         { label: 'Very Tight Disposable',              unit: '%',  multiplier: 100 },
    very_tight_adjustment:             { label: 'Very Tight Adjustment',              unit: '×'    },
    tight_disposable_pct:              { label: 'Tight Disposable',                   unit: '%',  multiplier: 100 },
    tight_adjustment:                  { label: 'Tight Adjustment',                   unit: '×'    },
    // ── Compliance ───────────────────────────────────────────────────────────
    equity_ter_threshold:              { label: 'Equity TER Threshold',               unit: '%'    },
    bond_ter_threshold:                { label: 'Bond TER Threshold',                 unit: '%'    },
    cash_ter_threshold:                { label: 'Cash TER Threshold',                 unit: '%'    },
    mixed_ter_threshold:               { label: 'Mixed TER Threshold',                unit: '%'    },
    alternative_ter_threshold:         { label: 'Alternative TER Threshold',          unit: '%'    },
    max_single_fund_allocation:        { label: 'Max Single Fund Allocation',         unit: '%',  multiplier: 100 },
    min_diversification_funds:         { label: 'Min Diversification Funds',          unit: ''     },
    srri_mismatch_tolerance:           { label: 'SRRI Mismatch Tolerance',            unit: ''     },
    require_kiid:                      { label: 'Require KIID',                       type: 'bool' },
    require_suitability_letter:        { label: 'Require Suitability Letter',         type: 'bool' },
    cool_off_period_days:              { label: 'Cool-Off Period',                    unit: 'days' },
};

function stepFor(meta) {
    if (meta.type === 'bool') return undefined;
    if (meta.unit === '£' || meta.unit === 'yrs' || meta.unit === 'days' || meta.unit === '') return 1;
    if (meta.unit === 'x') return 0.01;
    return 0.1; // %
}

function SettingRow({ catId, fieldKey, rawValue, meta, onChange }) {
    if (meta.type === 'bool') {
        return (
            <div className="d-flex justify-content-between align-items-center py-2 px-1"
                style={{ borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: 13 }}>{meta.label}</span>
                <Form.Check
                    type="switch"
                    id={`setting-${catId}-${fieldKey}`}
                    checked={!!rawValue}
                    onChange={e => onChange(catId, fieldKey, e.target.checked)}
                />
            </div>
        );
    }

    if (meta.type === 'select') {
        return (
            <div className="d-flex justify-content-between align-items-center py-2 px-1"
                style={{ borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: 13 }}>{meta.label}</span>
                <Form.Select
                    size="sm"
                    data-testid={`setting-${catId}-${fieldKey}`}
                    value={rawValue}
                    onChange={e => onChange(catId, fieldKey, e.target.value)}
                    style={{ width: 190, fontSize: 13 }}
                >
                    {meta.options.map(([val, lbl]) => (
                        <option key={val} value={val}>{lbl}</option>
                    ))}
                </Form.Select>
            </div>
        );
    }

    const displayVal = meta.multiplier ? rawValue * meta.multiplier : rawValue;
    const step = stepFor(meta);

    return (
        <div className="d-flex justify-content-between align-items-center py-2 px-1"
            style={{ borderBottom: '1px solid #f1f5f9' }}>
            <span style={{ fontSize: 13 }}>{meta.label}</span>
            <div className="d-flex align-items-center gap-2">
                <Form.Control
                    type="number"
                    size="sm"
                    data-testid={`setting-${catId}-${fieldKey}`}
                    value={displayVal}
                    step={step}
                    onChange={e => {
                        const parsed = parseFloat(e.target.value);
                        const raw = isNaN(parsed) ? 0 : (meta.multiplier ? parsed / meta.multiplier : parsed);
                        onChange(catId, fieldKey, raw);
                    }}
                    style={{ width: 110, textAlign: 'right', fontFamily: 'monospace', fontSize: 13 }}
                />
                {meta.unit && (
                    <span style={{ fontSize: 12, color: '#64748b', minWidth: 28 }}>{meta.unit}</span>
                )}
            </div>
        </div>
    );
}

function Settings() {
    const { can } = useAuth();
    const [settings, setSettings] = useState(null);
    const [activeTab, setActiveTab] = useState('tax');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [reloading, setReloading] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [feedback, setFeedback] = useState(null); // { type, msg }

    const load = useCallback(() => {
        setLoading(true);
        fetchSettings()
            .then(data => { setSettings(data); setDirty(false); })
            .catch(() => setFeedback({ type: 'danger', msg: 'Failed to load settings.' }))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleChange = (catId, key, value) => {
        setSettings(prev => ({ ...prev, [catId]: { ...prev[catId], [key]: value } }));
        setDirty(true);
        setFeedback(null);
    };

    const handleSave = async () => {
        setSaving(true);
        setFeedback(null);
        try {
            await saveSettings(settings);
            setDirty(false);
            setFeedback({ type: 'success', msg: 'Settings saved successfully.' });
        } catch (e) {
            const detail = e.response?.data?.detail;
            setFeedback({ type: 'danger', msg: Array.isArray(detail) ? detail[0]?.msg : (detail || 'Save failed.') });
        } finally {
            setSaving(false);
        }
    };

    const handleReload = async () => {
        setReloading(true);
        setFeedback(null);
        try {
            await reloadConfig();
            load();
            setFeedback({ type: 'info', msg: 'Configuration reloaded from disk.' });
        } catch (e) {
            setFeedback({ type: 'danger', msg: 'Reload failed.' });
        } finally {
            setReloading(false);
        }
    };

    if (loading) {
        return (
            <div className="page-content">
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                </div>
            </div>
        );
    }

    const catData = settings?.[activeTab] || {};

    // Split keys: known (in FIELD_META) rendered in definition order, unknown appended at the end
    const knownKeys = Object.keys(FIELD_META).filter(k => k in catData);
    const unknownKeys = Object.keys(catData).filter(k => !(k in FIELD_META));

    return (
        <div className="page-content">
            <div className="page-header">
                <div>
                    <h1 className="page-title">
                        Platform Settings
                        {dirty && (
                            <Badge bg="warning" text="dark" className="ms-2" style={{ fontSize: 11, verticalAlign: 'middle' }}>
                                Unsaved changes
                            </Badge>
                        )}
                    </h1>
                    <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                        Engine parameters — tax allowances, risk adjustments, compliance thresholds.
                        Changes take effect immediately on save.
                    </p>
                </div>
                <div className="d-flex gap-2">
                    <Button variant="outline-secondary" size="sm" onClick={handleReload} disabled={reloading || saving || !can.editAdviceConfig()}
                        title={can.editAdviceConfig() ? undefined : "Requires the waive-config role"}>
                        {reloading
                            ? <Spinner size="sm" animation="border" className="me-1" />
                            : <FontAwesomeIcon icon="rotate-right" className="me-1" />}
                        Reload from disk
                    </Button>
                    <Button variant="primary" size="sm" onClick={handleSave} disabled={saving || !dirty || !can.editAdviceConfig()}
                        title={can.editAdviceConfig() ? undefined : "Requires the waive-config role"}>
                        {saving && <Spinner size="sm" animation="border" className="me-1" />}
                        Save Changes
                    </Button>
                </div>
            </div>

            {feedback && (
                <Alert variant={feedback.type} className="mb-3 py-2" style={{ fontSize: 13 }}
                    dismissible onClose={() => setFeedback(null)}>
                    {feedback.msg}
                </Alert>
            )}

            {/* Category tabs */}
            <div className="d-flex gap-1 mb-3" style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: 8 }}>
                {CATEGORIES.map(cat => (
                    <button
                        key={cat.id}
                        onClick={() => setActiveTab(cat.id)}
                        style={{
                            background: activeTab === cat.id ? '#f1f5f9' : 'transparent',
                            border: 'none',
                            borderRadius: 6,
                            padding: '7px 14px',
                            fontSize: 13,
                            fontWeight: activeTab === cat.id ? 600 : 400,
                            color: activeTab === cat.id ? '#0f172a' : '#64748b',
                            cursor: 'pointer',
                        }}
                    >
                        {cat.label}
                    </button>
                ))}
            </div>

            <Card className="border-0 shadow-sm">
                <Card.Body style={{ padding: '0 24px' }}>
                    {knownKeys.map(key => (
                        <SettingRow
                            key={key}
                            catId={activeTab}
                            fieldKey={key}
                            rawValue={catData[key]}
                            meta={FIELD_META[key]}
                            onChange={handleChange}
                        />
                    ))}
                    {unknownKeys.map(key => (
                        <div key={key} className="d-flex justify-content-between align-items-center py-2 px-1"
                            style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <span style={{ fontSize: 13, fontFamily: 'monospace', color: '#64748b' }}>{key}</span>
                            <Form.Control
                                type="number"
                                size="sm"
                                value={catData[key]}
                                step="any"
                                onChange={e => handleChange(activeTab, key, parseFloat(e.target.value) || 0)}
                                style={{ width: 110, textAlign: 'right', fontFamily: 'monospace', fontSize: 13 }}
                            />
                        </div>
                    ))}
                </Card.Body>
            </Card>
        </div>
    );
}

export default Settings;
